import { Box, Divider, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { FormikProps, useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import toast from 'react-hot-toast';
import StyledBackButton from '../Common/StyledBackButton';
import StyledButton from '../Common/StyledButton';
import Steps, { steps } from './Steps';
import LinkCalendarStep from './LinkCalendar/LinkCalendarStep';
import SetAvailabilityStep from './SetAvailability/SetAvailabilityStep';
import OnlineConsultationsStep from './OnlineConsultations/OnlineConsultationsStep';
import InPersonConsultationsStep from './InPersonConsultations/InPersonConsultationsStep';
import { FormValues, validationSchema } from '../../utils/validations/officeDetailsForm';
import {
  createCoachAvailability,
  resetCreateCoachAvailability,
  createConsultationPreference,
  saveOfficedetails,
  resetCreateConsultationPreference,
  resetSaveOfficedetails,
  checkUrlExistence,
  resetCheckUrlExistence,
  saveBookingPageUrl,
  updateCoachInpersonConsultationPreference,
  resetVideoPlatforAuthURI,
  resetSaveLinkedVideoCallPlatform,
  updateCoachActiveStep,
} from '../../redux/booking/bookingSlice';
import { useAppSelector } from '../../redux/hooks';
import {
  days,
  initialTimes,
  initialVisibility,
  isDisabledBookingModalNextButton,
  mapFromAvailability,
  mapToIAvailability,
} from '../../utils/booking';
import { TokenTypeEnum, DaysOfWeek, DaysOfWeekSchedule } from '../../types/booking';
import { VideoCallPlatform } from '../../redux/coach/coachSlice';
import SuccessBookingSetup from './SuccessBookingSetup';
import LoadingButton from '../Common/LoadingButton';
import CoachServices from '../AddCoachService/CoachServices';
import SetURLStep from './SetURL/SetUrlOption';
import isValidUsername from '../../utils/validations/userNameValidation';
import validateTimes from '../../utils/validations/coachAvailabilityValidation';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';
import { toastStyles } from '../../utils/utils';

interface IBookingsModalProps {
  closeModel: () => void;
}

function BookingsModal({ closeModel }: IBookingsModalProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const coach = useAppSelector((state) => state.coach.coach);
  const {
    createCoachAvailabilityLoading,
    createCoachAvailabilitySuccess,
    isTokenExists,
    createConsultationPreferenceSuccess,
    createConsultationPreferenceLoading,
    saveOfficedetailsLoading,
    saveOfficedetailsSuccess,
    saveBookingPageUrlSuccess,
    saveBookingPageUrlLoading,
    fetchCoachAvailabilitiesLoading,
    coachConfigurations,
    urlExistStatus,
    urlExistError,
    isVideoPlatformTokenExists,
    videoPlatformUsername,
  } = useAppSelector((state) => state.booking);
  const coachAvailabilities = useAppSelector((state) => state.booking.coachAvailabilities);
  const { status, schedule } = mapFromAvailability(coachAvailabilities || []);

  const coachServicePackagesLoading = useAppSelector(
    (state) => state.coach.coachServicePackagesLoading
  );

  const servicePackages = useAppSelector((state) => state.coach.coachServicePackages);
  const hasServicePackages = servicePackages.length > 0;

  const [activeStep, setActiveStep] = useState(coachConfigurations.activeStep);
  const [maxActiveStep, setMaxActiveStep] = useState<number>(coachConfigurations.activeStep);
  const [timePickerVisibility, setTimePickerVisibility] = useState<DaysOfWeek | {}>(
    coachAvailabilities && coachAvailabilities?.length > 0 ? status : initialVisibility
  );
  const [times, setTimes] = useState<DaysOfWeekSchedule | {}>(
    coachAvailabilities && coachAvailabilities?.length > 0 ? schedule : initialTimes
  );
  const [isProvideOnlineConsultations, setIsProvideOnlineConsultations] = useState<boolean | null>(
    coachConfigurations.isProvideOnlineConsultations
  );
  const [isProvideInPersonConsultations, setIsProvideInPersonConsultations] = useState<
    boolean | null
  >(coachConfigurations.isProvideInPersonConsultations);

  const initialCalendarId = (() => {
    if (coachConfigurations.calendar === TokenTypeEnum.GOOGLE_CALENDER) {
      return 1;
    }
    if (coachConfigurations.calendar === TokenTypeEnum.MICROSOFT_CALENDAR) {
      return 2;
    }
    return null;
  })();

  const [selectedOption, setSelectedOption] = useState<number | null>(initialCalendarId);
  const initialPlatformId = (() => {
    if (
      coachConfigurations.videoCallPlatform === VideoCallPlatform.GOOGLE_MEET ||
      coachConfigurations.calendar === TokenTypeEnum.GOOGLE_CALENDER
    ) {
      return 1;
    }
    if (coachConfigurations.videoCallPlatform === VideoCallPlatform.ZOOM) {
      return 2;
    }
    return null;
  })();

  const [selectedVideoCallPlatformId, setSelectedVideoCallPlatformId] = useState<number | null>(
    initialPlatformId
  );
  const [url, setUrl] = useState<string>('');
  const [availabilitiesError, setAvailabilitiesError] = useState<Record<string, boolean>>({});
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  useEffect(() => {
    setMaxActiveStep(coachConfigurations.activeStep);
  }, [coachConfigurations.activeStep]);

  useEffect(() => {
    if (activeStep === 1 && createCoachAvailabilitySuccess) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      dispatch(resetCreateCoachAvailability());
    } else if (activeStep === 2 && createConsultationPreferenceSuccess) {
      dispatch(resetCreateConsultationPreference());
      dispatch(resetVideoPlatforAuthURI());
      dispatch(resetSaveLinkedVideoCallPlatform());
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else if (activeStep === 3 && saveOfficedetailsSuccess) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      dispatch(resetSaveOfficedetails());
    }
  }, [
    createCoachAvailabilitySuccess,
    activeStep,
    createConsultationPreferenceSuccess,
    saveOfficedetailsSuccess,
    saveBookingPageUrlSuccess,
  ]);

  const checkUrlExistenceDebounce = debounce((URL: string) => {
    dispatch(resetCheckUrlExistence());
    if (isValidUsername(URL, t).isValid) {
      dispatch(checkUrlExistence(URL));
    }
  }, 1500);

  useEffect(() => {
    if (url) {
      checkUrlExistenceDebounce(url);
    }
    return () => {
      checkUrlExistenceDebounce.cancel();
      dispatch(resetCheckUrlExistence());
    };
  }, [url]);

  const initialValues: FormValues = {
    address: coachConfigurations.officeAddress || '',
    phoneNumber: coachConfigurations.officePhone,
    isAddressPublic: coachConfigurations.makeAddressPublic,
    isPhoneNumberPublic: coachConfigurations.makePhonePublic,
  };

  const formik: FormikProps<FormValues> = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {},
  });

  const handleNext = () => {
    if (activeStep === 0) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else if (activeStep === 1) {
      const availabilities = mapToIAvailability(timePickerVisibility, times);
      const hasNulledValues = availabilities.some((a) => !a.startTime || !a.endTime);
      if (hasNulledValues) {
        toast.error(t('bookings.validations.duration.invalid'), toastStyles.error);
        return;
      }
      if (
        JSON.stringify(status) !== JSON.stringify(timePickerVisibility) ||
        JSON.stringify(schedule) !== JSON.stringify(times)
      ) {
        dispatch(
          createCoachAvailability({ coachId: coach.id, availabilities, activeStep: activeStep + 1 })
        );
      } else {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    } else if (activeStep === 2) {
      let videoCallPlatform: VideoCallPlatform | undefined;
      if (isProvideOnlineConsultations) {
        if (selectedVideoCallPlatformId === 1) {
          videoCallPlatform = VideoCallPlatform.GOOGLE_MEET;
        } else if (selectedVideoCallPlatformId === 2) {
          videoCallPlatform = VideoCallPlatform.ZOOM;
        }
      }

      let videoCallPlatformUsername: string | undefined;
      if (coachConfigurations.videoCallPlatformUsername) {
        videoCallPlatformUsername = coachConfigurations.videoCallPlatformUsername;
      } else if (videoPlatformUsername) {
        videoCallPlatformUsername = videoPlatformUsername;
      }

      dispatch(
        createConsultationPreference({
          activeStep: activeStep + 1,
          coachId: coach.id,
          isProvideOnlineConsultations: isProvideOnlineConsultations || false,
          videoCallPlatform,
          videoCallPlatformUsername,
        })
      );
    } else if (activeStep === 3) {
      if (isProvideInPersonConsultations === null) return;
      const { makePhonePublic, makeAddressPublic, officeAddress, officePhone } =
        coachConfigurations;
      if (
        makePhonePublic === formik.values.isPhoneNumberPublic &&
        makeAddressPublic === formik.values.isAddressPublic &&
        officeAddress === formik.values.address &&
        officePhone === formik.values.phoneNumber
      ) {
        dispatch(resetSaveOfficedetails());
        dispatch(updateCoachInpersonConsultationPreference(isProvideInPersonConsultations));
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      } else {
        dispatch(
          saveOfficedetails({
            coachId: coach.id,
            activeStep: activeStep + 1,
            officeAddress: formik.values.address,
            officePhone: formik.values.phoneNumber || '',
            makeAddressPublic: formik.values.isAddressPublic,
            makePhonePublic: formik.values.isPhoneNumberPublic,
            isProvideInPersonConsultations,
          })
        );
      }
    } else if (activeStep === 4) {
      dispatch(
        updateCoachActiveStep({
          activeStep: activeStep + 1,
        })
      );
      setActiveStep(activeStep + 1);
    } else if (activeStep === 5) {
      dispatch(
        saveBookingPageUrl({
          coachId: coach.id,
          activeStep: -1,
          url: url.trim(),
        })
      );
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    const newErrors = days.reduce((acc, day) => {
      if (timePickerVisibility[day]) {
        return {
          ...acc,
          [day]: !validateTimes({ [day]: times[day] }, { [day]: timePickerVisibility[day] }),
        };
      }
      return acc;
    }, {});
    setAvailabilitiesError(newErrors);
  }, [times, timePickerVisibility]);

  const disbaled =
    !checkPermissions(UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS) ||
    isDisabledBookingModalNextButton(
      activeStep,
      isProvideOnlineConsultations,
      isTokenExists,
      availabilitiesError,
      selectedVideoCallPlatformId,
      isVideoPlatformTokenExists,
      isProvideInPersonConsultations,
      hasServicePackages,
      url,
      urlExistStatus,
      formik
    );

  if (saveBookingPageUrlSuccess) {
    return <SuccessBookingSetup closeModel={closeModel} url={url} />;
  }

  return (
    <Grid
      boxSizing="border-box"
      display="flex"
      flexDirection="row"
      width="100%"
      height="90vh"
      minWidth="70vw"
    >
      <Grid item xs={isSmallScreen ? 2.6 : 2.3}>
        <Steps
          activeStep={activeStep}
          isProvideOnlineConsultations={isProvideOnlineConsultations}
          setActiveStep={setActiveStep}
          maxActiveStep={maxActiveStep}
        />
      </Grid>
      <Divider sx={{ borderColor: 'rgba(124, 174, 190, 0.25)' }} orientation="vertical" />
      <Grid item xs={isSmallScreen ? 9.4 : 9.7}>
        <Grid
          display="flex"
          justifyContent={language.split('-')[0] === 'ar' ? 'flex-start' : 'flex-end'}
          onClick={closeModel}
          right={language.split('-')[0] === 'ar' ? '' : 16}
          top={12}
          left={language.split('-')[0] === 'ar' ? 16 : 0}
          position="absolute"
        >
          <img
            src="/assets/icons/close-circle-large.svg"
            alt="close-icon"
            role="presentation"
            style={{
              cursor: 'pointer',
            }}
            onClick={closeModel}
          />
        </Grid>
        <Grid p="32px 24px 24px 24px" height="100%">
          {/* Plug the respective component for the step here. */}
          {activeStep === 0 && (
            <LinkCalendarStep
              setSelectedOption={setSelectedOption}
              selectedOption={selectedOption}
              hasPermissions={checkPermissions(
                UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS
              )}
            />
          )}
          {activeStep === 1 && (
            <SetAvailabilityStep
              timePickerVisibility={timePickerVisibility}
              setTimePickerVisibility={setTimePickerVisibility}
              times={times}
              setTimes={setTimes}
              availabilitiesError={availabilitiesError}
              hasPermissions={checkPermissions(
                UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS
              )}
            />
          )}
          {activeStep === 2 && (
            <OnlineConsultationsStep
              isProvideOnlineConsultations={isProvideOnlineConsultations}
              setIsProvideOnlineConsultations={setIsProvideOnlineConsultations}
              selectedOption={selectedVideoCallPlatformId}
              setSelectedOption={setSelectedVideoCallPlatformId}
              hasPermissions={checkPermissions(
                UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS
              )}
            />
          )}
          {activeStep === 3 && (
            <InPersonConsultationsStep
              isProvideInPersonConsultations={isProvideInPersonConsultations}
              setIsProvideInPersonConsultations={setIsProvideInPersonConsultations}
              isProvideOnlineConsultations={isProvideOnlineConsultations}
              formik={formik}
              hasPermissions={checkPermissions(
                UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS
              )}
            />
          )}
          {activeStep === 4 && <CoachServices activeStep={activeStep} />}
          {activeStep === 5 && (
            <SetURLStep
              url={url}
              setUrl={setUrl}
              urlExistStatus={urlExistStatus}
              urlExistError={urlExistError}
              hasPermissions={checkPermissions(
                UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS
              )}
            />
          )}
          <Divider />
          <Box display="flex" alignItems="center" justifyContent="space-between" mt="20px">
            {fetchCoachAvailabilitiesLoading || coachServicePackagesLoading ? (
              <LoadingButton paddingX={54} paddingY={11}>
                {t('common.back')}
              </LoadingButton>
            ) : (
              <StyledBackButton
                variant="outlined"
                onClick={activeStep === 0 ? closeModel : handleBack}
                sx={{ padding: '11px 54px !important' }}
              >
                <span>{t('common.back')}</span>
              </StyledBackButton>
            )}

            <StyledButton
              onClick={handleNext}
              paddingX={60}
              paddingY={14}
              disabled={disbaled}
              loading={
                createCoachAvailabilityLoading ||
                createConsultationPreferenceLoading ||
                saveOfficedetailsLoading ||
                saveBookingPageUrlLoading
              }
              loadingText={
                activeStep === steps.length - 1
                  ? t('bookings.finishing')
                  : t('therapistOnboarding.next')
              }
            >
              {activeStep === steps.length - 1
                ? t('bookings.finish')
                : t('therapistOnboarding.next')}
            </StyledButton>
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default BookingsModal;
